import React, { useEffect, useRef, useState } from 'react';
import { Send, Bot, User, Loader2, FileText, Check } from 'lucide-react';
import '../ChatInterface/ChatInterface.css';
import { createChatMessage, ChatMessage } from '../../types/chatTypes';
import { useApi } from '../../contexts/ApiContext';
import { useWithTokenRefresh } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import geminiService from '../../services/geminiService';

interface SupportChatProps {
  onClose: () => void;
  isInline?: boolean;
}

const SupportChat: React.FC<SupportChatProps> = ({ onClose, isInline = false }) => {
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  const { authState } = useAuth();

  const [messages, setMessages] = useState<ChatMessage[]>([
    createChatMessage(
      'assistant',
      "Hi! You're chatting with Flashback Support. Tell us what's going on and we'll get back to you shortly."
    ),
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [ackSent, setAckSent] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [summaryText, setSummaryText] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-focus input when the chat opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isSending) return;
    const userMsg = createChatMessage('user', inputMessage.trim());
    setMessages((prev) => [...prev, userMsg]);
    setInputMessage('');
    // Start AI triage streaming
    setIsLoading(true);
    setStreamingMessage('');
    try {
      const response = await geminiService.generateSupportStream(
        userMsg.content,
        messages,
        (chunk, fullText) => {
          setStreamingMessage(fullText);
        }
      );

      // Append assistant message with the final text
      const finalText = response.message;
      setMessages((prev) => [...prev, createChatMessage('assistant', finalText)]);

      // Detect summary between <summary>...</summary>
      const match = finalText.match(/<summary>[\s\S]*?<\/summary>/i);
      if (match) {
        const raw = match[0];
        const clean = raw.replace(/<\/?summary>/gi, '').trim();
        setSummaryText(clean);
      }

      if (!ackSent) setAckSent(true);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        createChatMessage(
          'assistant',
          'An error occurred while processing your message. Please try again later.',
          null,
          { isError: true }
        ),
      ]);
    } finally {
      setIsLoading(false);
      setStreamingMessage('');
    }
  };

  const getTranscriptText = () => {
    return messages.map((m) => `${m.role.toUpperCase()}: ${m.content}`).join('\n');
  };

  const handleSendToSupport = async () => {
    try {
      setIsSending(true);
      const email = authState?.user?.email || '';
      const subject = 'Support request from in-app chat';
      const message = summaryText || getTranscriptText();
      const formData = new FormData();
      formData.append('email', email);
      formData.append('subject', subject);
      formData.append('message', message);
      const resp = await withTokenRefresh(() => apiClient.sendFeedbackEmail(formData));
      const success = (resp as any)?.success === true;
      const feedback = success
        ? 'Thanks! Your message has been sent to our support team. We will get back to you soon.'
        : 'We could not send your message at this time. Please try again later.';
      setMessages((prev) => [
        ...prev,
        createChatMessage('assistant', feedback, null, { isError: !success }),
      ]);
      if (success) {
        setSummaryText(null);
      }
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        createChatMessage(
          'assistant',
          'An error occurred while sending your message. Please try again later.',
          null,
          { isError: true }
        ),
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getInputPlaceholder = () => 'Type your message...';

  return (
    <div className={`chat-interface ${isInline ? 'inline-mode' : ''}`}>
      {!isInline && (
        <div className="chat-header">
          <div className="header-content">
            <div className="header-left">
              <Bot size={24} className="header-icon" />
              <div>
                <h1 className="header-title">Flashback Support</h1>
                <p className="header-subtitle">Send us a message</p>
              </div>
            </div>
            <div className="header-actions" style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleSendToSupport}
                className="close-button"
                disabled={isSending || messages.length < 2}
              >
                {isSending ? 'Sending…' : 'Send to support'}
              </button>
              <button onClick={onClose} className="close-button">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="m18 6-12 12" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="messages-container">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`message-wrapper ${
              message.role === 'user' ? 'user-message' : 'assistant-message'
            }`}
          >
            <div
              className={`message-content ${
                message.role === 'user' ? 'user-content' : 'assistant-content'
              }`}
            >
              <div
                className={`avatar ${
                  message.role === 'user'
                    ? 'user-avatar'
                    : message.isError
                    ? 'error-avatar'
                    : 'assistant-avatar'
                }`}
              >
                {message.role === 'user' ? (
                  <User size={20} color="white" />
                ) : (
                  <Bot size={20} color="white" />
                )}
              </div>
              <div
                className={`message-bubble ${
                  message.role === 'user'
                    ? 'user-bubble'
                    : message.isError
                    ? 'error-bubble'
                    : 'assistant-bubble'
                }`}
              >
                <div className="message-text">{message.content}</div>
                <div
                  className={`message-time ${
                    message.role === 'user'
                      ? 'user-time'
                      : message.isError
                      ? 'error-time'
                      : 'assistant-time'
                  }`}
                >
                  {formatTime(message.timestamp)}
                </div>
              </div>
            </div>
          </div>
        ))}
        {(isLoading || (isSending && streamingMessage)) && streamingMessage && (
          <div className="message-wrapper assistant-message">
            <div className="message-content assistant-content">
              <div className="avatar assistant-avatar">
                <Bot size={20} color="white" />
              </div>
              <div className="message-bubble assistant-bubble">
                <div className="message-text">{streamingMessage}</div>
                <div className="typing-status">
                  <Loader2 size={12} className="animate-spin" />
                  <span>Typing...</span>
                </div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="input-container">
        <div className="input-wrapper">
          <div className="textarea-container">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={getInputPlaceholder()}
              className="message-input"
              rows={1}
              style={{ minHeight: '48px', maxHeight: '120px' }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, 120) + 'px';
              }}
            />
          </div>
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isSending}
            className="send-button"
          >
            {isSending ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
          </button>
        </div>
        {/* Summary validation and action */}
        {summaryText && (
          <div className="mt-2 p-3 rounded-md border border-gray-200 bg-white">
            <div className="flex items-center gap-2 mb-2 text-gray-800 font-medium">
              <FileText size={16} />
              <span>Summary to send</span>
            </div>
            <pre className="whitespace-pre-wrap text-sm text-gray-700">{summaryText}</pre>
            <div className="flex gap-8 justify-end mt-2">
              <button
                onClick={() => setSummaryText(null)}
                className="close-button"
                disabled={isSending}
              >
                Edit
              </button>
              <button
                onClick={handleSendToSupport}
                className="close-button"
                disabled={isSending}
                title="Send this summary to support"
              >
                {isSending ? (
                  'Sending…'
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Check size={16} /> Send to support
                  </span>
                )}
              </button>
            </div>
          </div>
        )}
        {/* Inline action button to submit to support */}
        {isInline && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              onClick={handleSendToSupport}
              className="close-button"
              disabled={isSending || messages.length < 2}
            >
              {isSending ? 'Sending…' : 'Send to support'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportChat;
